import React from 'react';
import PropTypes from 'prop-types';
import { Button, Alert } from 'reactstrap';
import RadioButtonForm from 'core/RadioButton';
import { maskEmail, maskPhoneNumber } from 'utils/maskingUtils';

/**
 * Shared UI for email / phone contact selection (Verify Identity and Select Primary Login).
 *
 * {@code single}: one selected value across phones + emails (shared name = mutually exclusive).
 * {@code perChannel}: independent email and phone selections.
 */
const EmailPhoneContactSelect = ({
  selectionMode,
  emails,
  phoneNumbers,
  headerContent,
  emailSectionLabel,
  phoneSectionLabel,
  maskEmail: maskEmailFlag,
  maskPhone: maskPhoneFlag,
  singleEmailReadOnly,
  selectedContact,
  onContactChange,
  selectedEmail,
  selectedPhone,
  onEmailChange,
  onPhoneChange,
  singleRadioGroupName,
  emailRadioName,
  phoneRadioName,
  footerNote,
  topAlert,
  errorAlert,
  emptyContactsMessage,
  primaryButtonId,
  onPrimaryClick,
  isLoading,
  primaryDisabled,
  showCancel,
  onCancel,
  cancelButtonId,
  buttonLayout,
}) => {
  const hasNoContacts = emails.length === 0 && phoneNumbers.length === 0;

  const showSingleEmailReadOnly =
    selectionMode === 'single' &&
    singleEmailReadOnly &&
    emails.length === 1 &&
    phoneNumbers.length === 0;

  const buildEmailOptions = () =>
    emails.map((email) => ({
      value: email.value,
      label: maskEmailFlag ? (
        <b id="email" style={{ fontSize: '13px' }}>
          {maskEmail(email.value)}
        </b>
      ) : (
        <span id="email" style={{ fontSize: '14px' }}>
          <div>{email.value}</div>
        </span>
      ),
    }));

  const buildPhoneOptions = () =>
    phoneNumbers.map((phone) => ({
      value: phone.value,
      label: maskPhoneFlag ? (
        <b id="phone" style={{ fontSize: '13px' }}>
          {maskPhoneNumber(phone.value)}
        </b>
      ) : (
        <span id="phone" style={{ fontSize: '14px' }}>
          <div>{phone.value}</div>
        </span>
      ),
    }));

  const phoneRadioNameResolved =
    selectionMode === 'single' ? singleRadioGroupName : phoneRadioName;
  const emailRadioNameResolved =
    selectionMode === 'single' ? singleRadioGroupName : emailRadioName;

  return (
    <div>
      {topAlert}

      {headerContent}

      {errorAlert && (
        <Alert color="danger" className="mb-3">
          {errorAlert}
        </Alert>
      )}

      {hasNoContacts && emptyContactsMessage && (
        <Alert color="warning">{emptyContactsMessage}</Alert>
      )}

      {!hasNoContacts && phoneNumbers.length > 0 && (
        <div className={selectionMode === 'perChannel' ? 'mb-3' : undefined}>
          <div className={selectionMode === 'perChannel' ? 'pb-2' : 'pb-1'}>
            {phoneSectionLabel}
          </div>
          <RadioButtonForm
            name={phoneRadioNameResolved}
            id="phones"
            options={buildPhoneOptions()}
            selectedOption={
              selectionMode === 'single' ? selectedContact : selectedPhone
            }
            onChange={
              selectionMode === 'single' ? onContactChange : onPhoneChange
            }
          />
        </div>
      )}

      {!hasNoContacts && emails.length > 0 && (
        <div className={selectionMode === 'perChannel' ? 'mb-3' : 'py-2'}>
          {showSingleEmailReadOnly ? (
            <p className="pl-2">
              {maskEmailFlag ? maskEmail(emails[0].value) : emails[0].value}
            </p>
          ) : (
            <>
              <div className={selectionMode === 'perChannel' ? 'pb-2' : 'pb-1'}>
                {emailSectionLabel}
              </div>
              <RadioButtonForm
                name={emailRadioNameResolved}
                id="emails"
                options={buildEmailOptions()}
                selectedOption={
                  selectionMode === 'single' ? selectedContact : selectedEmail
                }
                onChange={
                  selectionMode === 'single' ? onContactChange : onEmailChange
                }
              />
            </>
          )}
        </div>
      )}

      {footerNote && <p>{footerNote}</p>}

      {buttonLayout === 'stack' ? (
        <Button
          id={primaryButtonId}
          className="btn"
          block
          color="primary"
          type="button"
          disabled={isLoading || primaryDisabled}
          onClick={onPrimaryClick}
        >
          Continue
        </Button>
      ) : (
        <div className="d-flex mt-4" style={{ gap: '10px' }}>
          <Button
            id={primaryButtonId}
            color="primary"
            onClick={onPrimaryClick}
            disabled={isLoading || primaryDisabled}
            style={{ flex: 1 }}
          >
            Continue
          </Button>
          {showCancel && (
            <Button
              id={cancelButtonId}
              color="link"
              onClick={onCancel}
              disabled={isLoading}
              style={{ flex: 1 }}
            >
              Cancel
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

EmailPhoneContactSelect.propTypes = {
  selectionMode: PropTypes.oneOf(['single', 'perChannel']).isRequired,
  emails: PropTypes.array,
  phoneNumbers: PropTypes.array,
  headerContent: PropTypes.node,
  emailSectionLabel: PropTypes.string,
  phoneSectionLabel: PropTypes.string,
  maskEmail: PropTypes.bool,
  maskPhone: PropTypes.bool,
  singleEmailReadOnly: PropTypes.bool,
  selectedContact: PropTypes.string,
  onContactChange: PropTypes.func,
  selectedEmail: PropTypes.string,
  selectedPhone: PropTypes.string,
  onEmailChange: PropTypes.func,
  onPhoneChange: PropTypes.func,
  singleRadioGroupName: PropTypes.string,
  emailRadioName: PropTypes.string,
  phoneRadioName: PropTypes.string,
  footerNote: PropTypes.node,
  topAlert: PropTypes.node,
  errorAlert: PropTypes.string,
  emptyContactsMessage: PropTypes.string,
  primaryButtonId: PropTypes.string.isRequired,
  onPrimaryClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  primaryDisabled: PropTypes.bool,
  showCancel: PropTypes.bool,
  onCancel: PropTypes.func,
  cancelButtonId: PropTypes.string,
  buttonLayout: PropTypes.oneOf(['stack', 'split']),
};

EmailPhoneContactSelect.defaultProps = {
  emails: [],
  phoneNumbers: [],
  maskEmail: false,
  maskPhone: false,
  singleEmailReadOnly: false,
  singleRadioGroupName: 'verificationOption',
  emailRadioName: 'selectPrimaryLoginEmail',
  phoneRadioName: 'selectPrimaryLoginPhone',
  isLoading: false,
  primaryDisabled: false,
  showCancel: false,
  buttonLayout: 'stack',
};

export default EmailPhoneContactSelect;
